import { useEffect, useMemo, useRef, useState } from "react";
import type { Accordion, Asset } from "@/types/deal";

export const ACCORDION_STATUSES_HIDDEN_FROM_MAP = [
  "Killed",
  "Lost to Other Lender",
  "Referred to Apollo",
] as string[];

/**
 * Shared accordion → asset filtering for deal maps (main asset map and comps map).
 *
 * @param showInitialDealAssets When `undefined` (comps map), assets not linked to any
 *   visible accordion remain visible. When `boolean` (deal asset map), that group is
 *   gated by the toggle.
 */
export function useAccordionFilteredMapAssets(
  assets: Asset | Asset[],
  accordions: Accordion[] | undefined,
  showInitialDealAssets?: boolean,
) {
  const visibleAccordions = useMemo(() => {
    if (!accordions || accordions.length === 0) return [];
    return accordions.filter((acc) => {
      const statusName =
        typeof acc.status === "object" && acc.status != null
          ? acc.status.status
          : null;
      return (
        statusName == null ||
        !ACCORDION_STATUSES_HIDDEN_FROM_MAP.includes(statusName)
      );
    });
  }, [accordions]);

  const hiddenAccordionAssetIds = useMemo(() => {
    if (!accordions || accordions.length === 0) return new Set<number>();
    const ids = new Set<number>();
    accordions.forEach((acc) => {
      const statusName =
        typeof acc.status === "object" && acc.status != null
          ? acc.status.status
          : null;
      if (
        statusName != null &&
        ACCORDION_STATUSES_HIDDEN_FROM_MAP.includes(statusName) &&
        acc.asset_ids
      ) {
        acc.asset_ids.forEach((assetId) => ids.add(assetId));
      }
    });
    return ids;
  }, [accordions]);

  const [selectedAccordions, setSelectedAccordions] = useState<number[]>([]);
  const prevAccordionIdsStringRef = useRef<string>("");
  const initializedAccordionIdsRef = useRef<string>("");

  const accordionIds = useMemo(() => {
    if (!visibleAccordions || visibleAccordions.length === 0) return [];
    const ids = visibleAccordions.map((acc) => acc.id);
    const idsString = ids.sort((a, b) => a - b).join(",");
    if (idsString === prevAccordionIdsStringRef.current) {
      const cached = prevAccordionIdsStringRef.current
        .split(",")
        .filter((s) => s)
        .map(Number);
      return cached.length === ids.length ? cached : ids;
    }
    prevAccordionIdsStringRef.current = idsString;
    return ids;
  }, [
    visibleAccordions
      .map((acc) => acc.id)
      .sort((a, b) => a - b)
      .join(","),
  ]);

  useEffect(() => {
    const currentIdsString =
      accordionIds.length > 0
        ? [...accordionIds].sort((a, b) => a - b).join(",")
        : "";

    if (currentIdsString !== initializedAccordionIdsRef.current) {
      if (accordionIds.length > 0) {
        setSelectedAccordions([...accordionIds]);
        initializedAccordionIdsRef.current = currentIdsString;
      } else {
        setSelectedAccordions([]);
        initializedAccordionIdsRef.current = "";
      }
    }
  }, [accordionIds]);

  const filteredAssets = useMemo(() => {
    const assetArray = Array.isArray(assets) ? assets : [assets];

    const notFromHiddenAccordions = assetArray.filter(
      (asset) => asset.id != null && !hiddenAccordionAssetIds.has(asset.id),
    );

    if (!visibleAccordions || visibleAccordions.length === 0) {
      return notFromHiddenAccordions;
    }

    const selectedAccordionAssetIds = new Set<number>();
    visibleAccordions.forEach((accordion) => {
      if (selectedAccordions.includes(accordion.id) && accordion.asset_ids) {
        accordion.asset_ids.forEach((assetId) =>
          selectedAccordionAssetIds.add(assetId),
        );
      }
    });

    return notFromHiddenAccordions.filter((asset) => {
      if (!asset.id) return false;

      const belongsToSelectedAccordion = selectedAccordionAssetIds.has(
        asset.id,
      );

      const belongsToAnyAccordion = visibleAccordions.some(
        (acc) => acc.asset_ids && acc.asset_ids.includes(asset.id),
      );

      return (
        belongsToSelectedAccordion ||
        (!belongsToAnyAccordion &&
          (showInitialDealAssets === undefined ? true : showInitialDealAssets))
      );
    });
  }, [
    assets,
    visibleAccordions,
    selectedAccordions,
    hiddenAccordionAssetIds,
    showInitialDealAssets,
  ]);

  return {
    visibleAccordions,
    hiddenAccordionAssetIds,
    selectedAccordions,
    setSelectedAccordions,
    filteredAssets,
  };
}
